import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, UserCheck, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-2 py-12">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-navy-500 mb-6">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: January 2025</p>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <Shield className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Our Commitment to Privacy</h2>
                <p className="text-gray-700 mb-4">
                  At qbool, we respect your privacy and are committed to protecting your personal information. 
                  This Privacy Policy explains how we collect, use, and safeguard your data in accordance with 
                  international privacy standards and Islamic principles of trust and confidentiality.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <Eye className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>
                <ul className="space-y-3 text-gray-700">
                  <li><strong>Profile Information:</strong> Name, age, location, education, occupation, religious practices</li>
                  <li><strong>Contact Information:</strong> Email address, phone number</li>
                  <li><strong>Preferences:</strong> Partner preferences, search criteria</li>
                  <li><strong>Photos:</strong> Profile pictures you choose to upload</li>
                  <li><strong>Usage Data:</strong> How you interact with our platform</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <Lock className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
                <ul className="space-y-3 text-gray-700">
                  <li>• To create and manage your matrimonial profile</li>
                  <li>• To match you with compatible profiles</li>
                  <li>• To facilitate communication between members</li>
                  <li>• To improve our services and user experience</li>
                  <li>• To send important updates and notifications</li>
                  <li>• To verify profiles and maintain platform safety</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <UserCheck className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
                <p className="text-gray-700 mb-3">You have the right to:</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Access your personal data</li>
                  <li>• Correct inaccurate information</li>
                  <li>• Delete your account and data</li>
                  <li>• Control who can see your profile</li>
                  <li>• Opt-out of communications</li>
                  <li>• Download your data</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <Shield className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Data Security</h2>
                <p className="text-gray-700 mb-3">
                  We implement industry-standard security measures to protect your information:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Encrypted data transmission (SSL/TLS)</li>
                  <li>• Secure password storage</li>
                  <li>• Regular security audits</li>
                  <li>• Limited access to personal data</li>
                  <li>• Compliance with international privacy standards</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <div className="flex items-start mb-4">
              <AlertCircle className="w-6 h-6 text-navy-500 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-semibold mb-4">Information Sharing</h2>
                <p className="text-gray-700 mb-3">
                  We DO NOT sell your personal information. We only share your data:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• With other members you choose to connect with</li>
                  <li>• With service providers who help operate our platform</li>
                  <li>• When required by law</li>
                  <li>• To prevent fraud or abuse</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-6">
              If you have questions about this Privacy Policy or your data, we're here to help.
            </p>
            <Link 
              to="/support-messages"
              className="inline-flex items-center justify-center px-6 py-3 bg-mint-500 text-white font-semibold rounded-lg hover:bg-mint-600 transition-colors"
            >
              Contact Us
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPage;